import {
	ConvoAIWebSocketAPI,
	type ConvoAIWebSocketServerMessage,
	ConvoAIWebSocketServerMessageType,
} from '../../api/convo-ai/ConvoAIWebSocketAPI';

import { processAudioToTextWithPCM16 } from './processAudioToText';
import {
	VoiceDictationStopReason,
	type WebSpeechToTextAPIAction,
	type WebSpeechToTextTraceAction,
} from './WebSpeechToTextModels';

interface BrowserSpeechRecognitionEvent {
	resultIndex: number;
	results: {
		[index: number]: {
			[index: number]: {
				transcript: string;
			};
			isFinal: boolean;
			length: number;
		};
		length: number;
	};
}

interface BrowserSpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	onend: (() => void) | null;
	onerror: ((event: { error: string }) => void) | null;
	onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
	start(): void;
	stop(): void;
}

interface BrowserSpeechRecognitionConstructor {
	new (): BrowserSpeechRecognition;
}

const getBrowserSpeechRecognition = (): BrowserSpeechRecognitionConstructor | undefined => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const win = window as any;
	return win.webkitSpeechRecognition || win.SpeechRecognition;
};

const getSupportedMimeType = (): string | undefined => {
	// Order of preference: webm (best for streaming), then mp4/aac (Safari)
	const mimeTypes = [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/mp4',
		'audio/aac',
		'audio/ogg;codecs=opus',
	];

	// Access MediaRecorder via window to avoid lint errors for unsupported browsers
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const MediaRecorderAPI = (window as any).MediaRecorder as typeof MediaRecorder | undefined;

	// Check if MediaRecorder.isTypeSupported exists (not available in older browsers)
	if (!MediaRecorderAPI || typeof MediaRecorderAPI.isTypeSupported !== 'function') {
		return undefined;
	}

	return mimeTypes.find((mimeType) => MediaRecorderAPI.isTypeSupported(mimeType)) ?? undefined;
};

export type WebSpeechToTextManagerOptions = {
	audioBitsPerSecond?: number;
	cloudID: string;
	enableGhostText?: boolean;
	experience?: string;
	lang?: string;
	maxRecordingDurationMs?: number;
	onTrace?: (action: WebSpeechToTextTraceAction) => void;
	onTranscription: (action: WebSpeechToTextAPIAction) => void;
	product?: string;
	sampleRate?: number;
	timeSlice?: number;
};

export class WebSpeechToTextManager {
	private audioChunks: Blob[] = [];
	private lastProcessedPosition = 0;
	private processedChunksCountAfterLastCommit = 0;
	private convoAIWebSocketAPI: ConvoAIWebSocketAPI | null = null;
	private mediaStream: MediaStream | null = null;
	private mediaRecorder: MediaRecorder | null = null;
	private lastAudioChunkWasProcessed = false;
	private recordingTimeout: NodeJS.Timeout | null = null;
	private transcription: string | null = null;
	private chunkIndex = 0;
	private createdItemChunkIndex = 0;
	private recognition: SpeechRecognition | null = null;

	private readonly cloudID: string;
	private readonly enableGhostText: boolean;
	private readonly lang: string;
	private readonly sampleRate: number;
	private readonly audioBitsPerSecond: number;
	private readonly timeSlice: number;
	private readonly maxRecordingDurationMs: number;
	private readonly product: string;
	private readonly experience: string;
	private readonly onTrace?: (action: WebSpeechToTextTraceAction) => void;
	private readonly onTranscription: (action: WebSpeechToTextAPIAction) => void;

	constructor(options: WebSpeechToTextManagerOptions) {
		const {
			onTranscription,
			cloudID,
			enableGhostText = false,
			lang = 'en-US',
			sampleRate = 24000,
			audioBitsPerSecond = 16000,
			timeSlice = 1000,
			maxRecordingDurationMs = 60000,
			onTrace,
			product = 'confluence',
			experience = 'confluence-ai-first-creation',
		} = options;

		this.onTranscription = onTranscription;
		this.cloudID = cloudID;
		this.enableGhostText = enableGhostText;
		this.lang = lang;
		this.sampleRate = sampleRate;
		this.audioBitsPerSecond = audioBitsPerSecond;
		this.timeSlice = timeSlice;
		this.maxRecordingDurationMs = maxRecordingDurationMs;
		this.onTrace = onTrace;
		this.product = product;
		this.experience = experience;
	}

	public async startRecording(): Promise<void> {
		this.resetInMemoryState();

		this.onTranscription({ type: 'SET_INITIALIZING' });
		await this.connectWebSocketConvoAIAPI();

		this.mediaStream = await navigator.mediaDevices.getUserMedia({
			audio: {
				sampleRate: this.sampleRate,
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
			},
		});

		if (this.enableGhostText) {
			this.startBrowserSpeechRecognition();
		}

		// Detect supported audio format (Safari doesn't support webm)
		const mimeType = getSupportedMimeType();

		const mediaRecorderOptions: MediaRecorderOptions = {
			audioBitsPerSecond: this.audioBitsPerSecond,
		};
		if (mimeType) {
			mediaRecorderOptions.mimeType = mimeType;
		}
		// eslint-disable-next-line compat/compat
		this.mediaRecorder = new MediaRecorder(this.mediaStream, mediaRecorderOptions);

		this.mediaRecorder.start(this.timeSlice);

		this.mediaRecorder.addEventListener('dataavailable', this.onMediaRecorderDataAvailable);
		this.mediaRecorder.addEventListener('stop', this.onMediaRecorderStop);
		this.mediaRecorder.addEventListener('error', this.onMediaRecorderError);

		this.recordingTimeout = setTimeout(() => {
			this.stopRecording(VoiceDictationStopReason.TimeoutAccepted);
		}, this.maxRecordingDurationMs);
	}

	public stopRecording(
		stopReason: VoiceDictationStopReason = VoiceDictationStopReason.StoppedAccepted,
	): void {
		this.insertEmptySpaceIntoTranscription();
		this.clearRecordingTimeout();
		this.stopBrowserSpeechRecognition();
		this.onTrace?.({ type: 'STOP_RECORDING', payload: { stopReason } });
		this.onTranscription({ type: 'SET_STOPPED', payload: { stopReason } });
		this.mediaRecorder?.stop();
	}

	private startBrowserSpeechRecognition(): void {
		const SpeechRecognitionAPI = getBrowserSpeechRecognition();
		if (!SpeechRecognitionAPI) {
			return;
		}

		this.recognition = new SpeechRecognitionAPI();
		this.recognition.continuous = true;
		this.recognition.interimResults = true;
		this.recognition.lang = this.lang;

		this.recognition.onresult = (event: BrowserSpeechRecognitionEvent) => {
			// Rebuild ghost text from the full results array each time.
			// This avoids accumulation bugs across recognition restarts — when
			// recognition ends and restarts (onend → start()), the results array
			// resets naturally so no cross-session duplication is possible.
			let ghostTranscription = '';
			for (let i = 0; i < event.results.length; i++) {
				ghostTranscription += event.results[i][0].transcript;
			}
			this.onTranscription({
				type: 'SET_GHOST',
				payload: { ghostTranscription },
			});
		};

		this.recognition.onend = () => {
			if (this.mediaRecorder?.state === 'recording') {
				try {
					this.recognition?.start();
				} catch {
					// Ignore restart errors
				}
			}
		};

		this.recognition.onerror = (event: { error: string }) => {
			this.onTrace?.({
				type: 'ERROR',
				payload: { error: `Ghost text speech recognition error: ${event.error}` },
			});
		};

		try {
			this.recognition.start();
		} catch {
			// Browser doesn't support speech recognition - graceful fallback
			this.recognition = null;
		}
	}

	private stopBrowserSpeechRecognition(): void {
		if (this.recognition) {
			try {
				this.recognition.stop();
			} catch {
				// Ignore stop errors
			}
			this.recognition = null;
		}
	}

	private async connectWebSocketConvoAIAPI() {
		try {
			this.onTrace?.({ type: 'START_WEBSOCKET_CONNECTION' });
			this.convoAIWebSocketAPI = await ConvoAIWebSocketAPI.create(
				this.cloudID,
				this.product,
				this.experience,
				this.onMessage,
			);
			this.onTrace?.({ type: 'WEBSOCKET_CONNECTION_SUCCESS' });
			this.convoAIWebSocketAPI.updateTranscriptionConfig();
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
			this.onTrace?.({
				type: 'ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	}

	private insertEmptySpaceIntoTranscription() {
		if (this.transcription) {
			this.transcription = this.transcription + ' ';
			this.onTranscription({
				type: 'SET_RECORDING',
				payload: { transcription: this.transcription, chunkIndex: this.chunkIndex },
			});
		}
	}

	private onMessage = (event: ConvoAIWebSocketServerMessage) => {
		switch (event.type) {
			case ConvoAIWebSocketServerMessageType.TranscriptionResponseDelta:
				this.transcription =
					this.transcription !== null ? this.transcription + event.delta : event.delta;
				this.chunkIndex++;
				this.onTranscription({
					type: 'SET_RECORDING',
					payload: { transcription: this.transcription, chunkIndex: this.chunkIndex },
				});
				break;
			case ConvoAIWebSocketServerMessageType.TranscriptionItemCreated:
				this.createdItemChunkIndex++;
				this.onTrace?.({
					type: 'TRANSCRIPTION_ITEM_CREATED',
					payload: { createdItemChunkIndex: this.createdItemChunkIndex },
				});
				this.insertEmptySpaceIntoTranscription();
				break;
		}
	};

	private onMediaRecorderStop = () => {
		this.mediaStream?.getTracks().forEach((track) => track.stop());
		this.convoAIWebSocketAPI?.disconnect();
	};

	private onMediaRecorderError = (event: MediaRecorderEventMap['error']) => {
		this.onTranscription({ type: 'SET_ERROR', payload: { error: event.message } });
		this.onTrace?.({ type: 'ERROR', payload: { error: event.message } });
	};

	private onMediaRecorderDataAvailable = async (event: MediaRecorderEventMap['dataavailable']) => {
		this.audioChunks.push(event.data);

		try {
			const { audioString, newAudioEndPosition } = await processAudioToTextWithPCM16(
				this.audioChunks,
				this.lastProcessedPosition,
				this.sampleRate,
			);
			this.lastProcessedPosition = newAudioEndPosition;

			if (audioString) {
				this.lastAudioChunkWasProcessed = true;
				this.processedChunksCountAfterLastCommit++;
				this.convoAIWebSocketAPI?.sendAudio(audioString);
			} else if (audioString === null && this.lastAudioChunkWasProcessed) {
				this.convoAIWebSocketAPI?.commitAudio();
				this.processedChunksCountAfterLastCommit = 0;
				this.lastAudioChunkWasProcessed = false;
			}
		} catch (error) {
			this.onTranscription({
				type: 'SET_ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
			this.onTrace?.({
				type: 'ERROR',
				payload: { error: error instanceof Error ? error.message : 'Unknown error' },
			});
		}
	};

	private clearRecordingTimeout() {
		if (this.recordingTimeout) {
			clearTimeout(this.recordingTimeout);
			this.recordingTimeout = null;
		}
	}

	private resetInMemoryState() {
		this.chunkIndex = 0;
		this.createdItemChunkIndex = 0;
		this.lastProcessedPosition = 0;
		this.processedChunksCountAfterLastCommit = 0;
		this.lastAudioChunkWasProcessed = false;
		this.transcription = null;
		this.audioChunks = [];
		this.stopBrowserSpeechRecognition();
		this.clearRecordingTimeout();
	}
}
